import { createAsyncThunk } from "@reduxjs/toolkit";
import { difference } from "lodash";

import { updateUserSubscriptions } from "../../../common/api";
import { trackEvent } from "../../../common/tracking/trackerService";
import type {
  UserSubscriptionInput,
  UserSubscriptionsInput,
} from "../../../gql/graphql";
import type { StoreState } from "../../../store/storeState";
import type { UserSubscriptionsMutation_updateSubscriptionSettings } from "../../../use-cases/settings/graphql/gql/UserSubscriptionsMutation";
import {
  getUserSubscriptionsFormState,
  getUserSubscriptionsInitialFormState,
  type SubscriptionKey,
  type UserSubscription,
} from "../userSubscriptionsReducer";
import { trackChangedUserSubscriptions } from "../utils";

const transformStateToInput = ({
  settings,
  routeCodes,
}: {
  settings: Record<SubscriptionKey, UserSubscription>;
  routeCodes: string[];
}): UserSubscriptionsInput => {
  return {
    arrivalTimeChanged: transformUserSubscriptionToInput(
      settings.arrivalTimeChanged,
    ),
    bookingUpdatedInFreightPortal: transformUserSubscriptionToInput(
      settings.bookingUpdatedInFreightPortal,
    ),
    checkInDeviation: transformUserSubscriptionToInput(
      settings.checkInDeviation,
    ),
    dailySailingSummary: transformUserSubscriptionToInput(
      settings.dailySailingSummary,
    ),
    lateCancellations: transformUserSubscriptionToInput(
      settings.lateCancellations,
    ),
    noShow: transformUserSubscriptionToInput(settings.noShow),
    routeCodes,
    sailingCancelled: transformUserSubscriptionToInput(
      settings.sailingCancelled,
    ),
    unitsSummaryPerSailing: transformUserSubscriptionToInput(
      settings.unitsSummaryPerSailing,
    ),
    waitingChangedToConfirmed: transformUserSubscriptionToInput(
      settings.waitingChangedToConfirmed,
    ),
  };
};

const transformUserSubscriptionToInput = (
  subscription: UserSubscription,
): UserSubscriptionInput => {
  return {
    emailEnabled: subscription.emailEnabled,
    smsEnabled: subscription.smsEnabled,
    freightPortalEnabled: subscription.freightPortalEnabled,
  };
};

const trackUserSubscriptionsRouteChange = (items: string[], action: string) => {
  items.forEach((item) => {
    trackEvent(
      {
        category: "Subscriptions",
        action: action,
        label: item,
      },
      { route_code: item },
    );
  });
};

export const saveUserSubscriptions = createAsyncThunk<
  UserSubscriptionsMutation_updateSubscriptionSettings,
  void,
  { state: StoreState }
>("save-user-subscriptions", async (_, { getState }) => {
  const formState = getUserSubscriptionsFormState(getState());
  const initialFormState = getUserSubscriptionsInitialFormState(getState());

  const removedRoutes = difference(
    initialFormState.routeCodes,
    formState.routeCodes,
  );
  const addedRoutes = difference(
    formState.routeCodes,
    initialFormState.routeCodes,
  );

  trackChangedUserSubscriptions(initialFormState.settings, formState.settings);
  trackUserSubscriptionsRouteChange(addedRoutes, "Add Subscription Route");
  trackUserSubscriptionsRouteChange(removedRoutes, "Remove Subscription Route");

  return updateUserSubscriptions(
    transformStateToInput(getUserSubscriptionsFormState(getState())),
  );
});
